import type { ApolloError } from '@apollo/client';
import { useMutation as useApolloMutation, useQuery as useApolloQuery } from '@apollo/client';
import { defaultTo, isEmpty, isNull } from 'lodash-es';
import { useContext } from 'react';
import type { GlobalGraphQLErrorHandler } from './global-graphql-error-handler';
import { GraphQLErrorHandlerContext } from './global-graphql-error-handler';

type OnErrorOption = {
  onError?: (error: ApolloError) => void;
};

const addErrorHandler = <T extends OnErrorOption>(
  { onGraphQlError, onNetworkError }: GlobalGraphQLErrorHandler,
  options: T = {} as T,
): T => ({
    ...options,
    onError: defaultTo(options?.onError, error => {
      if (!isEmpty(error.graphQLErrors)) {
        onGraphQlError(error.graphQLErrors);
      }

      if (!isNull(error.networkError)) {
        onNetworkError(error.networkError);
      }
    }),
  });

export const useQuery: typeof useApolloQuery = (query, options) => {
  const { onGraphQlError, onNetworkError } = useContext(GraphQLErrorHandlerContext);
  return useApolloQuery(query, addErrorHandler({ onGraphQlError, onNetworkError }, options));
};

export const useMutation: typeof useApolloMutation = (mutation, options) => {
  const { onGraphQlError, onNetworkError } = useContext(GraphQLErrorHandlerContext);
  return useApolloMutation(mutation, addErrorHandler({ onGraphQlError, onNetworkError }, options));
};
